import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { FullscreenModal } from './fullscreen-modal';

interface AudioPreviewProps {
  fileUrl: string;
  fileName: string;
}

export const AudioPreview: React.FC<AudioPreviewProps> = ({ fileUrl, fileName }) => {
  const { t } = useTranslation();
  const [showPlayer, setShowPlayer] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const getFileIcon = () => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'mp3':
        return 'mdi:file-music';
      case 'wav':
        return 'mdi:file-music-outline';
      case 'ogg':
        return 'mdi:file-music';
      case 'aac':
        return 'mdi:file-music';
      case 'flac':
        return 'mdi:file-music';
      default:
        return 'mdi:file-music';
    }
  };

  const getFileTypeLabel = () => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'mp3':
        return t('forms.mp3Audio');
      case 'wav':
        return t('forms.wavAudio');
      case 'ogg':
        return t('forms.oggAudio');
      case 'aac':
        return t('forms.aacAudio');
      case 'flac':
        return t('forms.flacAudio');
      default:
        return t('forms.audioFile');
    }
  };

  const getAudioType = () => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'mp3':
        return 'audio/mpeg';
      case 'wav':
        return 'audio/wav';
      case 'ogg':
        return 'audio/ogg';
      case 'aac':
        return 'audio/aac';
      case 'flac':
        return 'audio/flac';
      default:
        return 'audio/mpeg';
    }
  };

  const handlePlayerToggle = () => {
    setShowPlayer(!showPlayer);
  };

  const handleFullscreenClick = () => {
    setIsFullscreen(true);
  };

  const handleCloseFullscreen = () => {
    setIsFullscreen(false);
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/70">
      <div className="flex items-center space-x-3">
        <div className="flex-shrink-0">
          <Icon icon={getFileIcon()} className="text-4xl text-purple-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate" title={fileName}>
            {fileName}
          </p>
          <p className="text-xs text-gray-500">{getFileTypeLabel()}</p>
        </div>
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={handlePlayerToggle}
            className="flex-shrink-0 px-3 py-1 bg-blue-100 text-blue-700 rounded-md text-sm hover:bg-blue-200 transition-colors"
          >
            {showPlayer ? t('forms.hidePlayer') : t('forms.play')}
          </button>
          <button
            type="button"
            onClick={handleFullscreenClick}
            className="flex-shrink-0 px-3 py-1 bg-purple-100 text-purple-700 rounded-md text-sm hover:bg-purple-200 transition-colors"
            title={t('forms.openInFullscreen')}
          >
            <Icon icon="mdi:fullscreen" className="text-sm" />
          </button>
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 px-3 py-1 bg-purple-100 text-purple-700 rounded-md text-sm hover:bg-purple-200 transition-colors"
          >
            {t('forms.download')}
          </a>
        </div>
      </div>
      
      {showPlayer && (
        <div className="mt-4 border-t border-gray-200 pt-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-900">{t('forms.audioPlayer')}</h4>
              <div className="flex items-center space-x-2 text-xs text-gray-500">
                <Icon icon="mdi:volume-high" className="text-sm" />
                <span>{t('forms.clickPlayToListen')}</span>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded border border-gray-200 p-4">
              <audio controls className="w-full h-10">
                <source src={fileUrl} type={getAudioType()} />
                <source src={fileUrl} type="audio/mpeg" />
                <source src={fileUrl} type="audio/wav" />
                Your browser does not support the audio element.
              </audio>
              <div className="mt-2 text-center">
                <p className="text-xs text-gray-500">{getFileTypeLabel()}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Modal */}
      <FullscreenModal
        isOpen={isFullscreen}
        onClose={handleCloseFullscreen}
        title={`${getFileTypeLabel()} - ${fileName}`}
      >
        <div className="h-full flex flex-col items-center justify-center bg-gray-100">
          <div className="text-center space-y-6 max-w-md">
            <Icon icon={getFileIcon()} className="text-8xl text-purple-500 mx-auto" />
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-2">{getFileTypeLabel()}</h3>
              <p className="text-gray-600 mb-6">{fileName}</p>
            </div>
            <audio controls className="w-full h-16">
              <source src={fileUrl} type={getAudioType()} />
              <source src={fileUrl} type="audio/mpeg" />
              <source src={fileUrl} type="audio/wav" />
              Your browser does not support the audio element.
            </audio>
            <div className="pt-4">
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-purple-600 text-white rounded-lg text-lg hover:bg-purple-700 transition-colors"
              >
                <Icon icon="mdi:download" className="mr-2" />
                {t('forms.download')}
              </a>
            </div>
          </div>
        </div>
      </FullscreenModal>
    </div>
  );
};


